import { configureStore } from "@reduxjs/toolkit";
import configReducer, { type ConfigSliceState } from "./reducers/configSlice";
import municipalityReducer, {
  type MunicipalitySliceState,
} from "./reducers/municipalitySlice";
import userReducer, { type UserSliceState } from "./reducers/userSlice";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistCombineReducers,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { isDev } from "@/helpers/misc";

export type StoreRootState = {
  user: UserSliceState;
  municipality: MunicipalitySliceState;
  config: ConfigSliceState;
};


const transformSliceState = (state: unknown, key: string) => {
  if (key === "user") {
    const userState = state as UserSliceState;
    return {
      data: userState.data,
      loading: userState.loading,
      isLoggedIn: userState.isLoggedIn,
    };
  }
  if (key === "municipality") {
    const municipalityState = state as MunicipalitySliceState;
    return {
      data: municipalityState.data,
      loading: false,
      activeId: municipalityState.activeId,
    };
  }
  if (key === "config") {
    const configState = state as ConfigSliceState;
    return {
      data: configState.data,
    };
  }
  return state;
};

const rootPersistTransform = {
  in: transformSliceState,
  out: transformSliceState,
}

const rootPersistConfig = {
  key: "root",
  storage,
  transforms: [rootPersistTransform],
};

// @ts-expect-error redux-persist accepts this runtime transform shape.
const persistedReducers = persistCombineReducers(rootPersistConfig, {
  user: userReducer,
  municipality: municipalityReducer,
  config: configReducer,
});

const store = configureStore({
  reducer: persistedReducers,
  devTools: isDev,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });
  },
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;

export default store;
